import { useWorkspaceText } from '../interface-copy';
import { useChatInterfaceLocale } from '@/lib/chat-i18n';
import { useNamespaceCopy } from '@/i18n/ns';
import runtimeCopy from '@/i18n/ns/workspace-knowledge-runtime';
import { useState } from 'react';
import { formatCopy } from '@/i18n/ns';
import workspaceFeaturesCopy from '@/i18n/ns/workspace-features';
import knowledgePagesCopy from '@/i18n/ns/knowledge-pages';
import editorCopy from '@/i18n/ns/workspace-editors';
import { WorkspaceIcon } from '../icons';
import { plural } from './format';
import { matchTitle, type TreeIndex } from './tree';
import type { TreePage } from './types';

function Count({ page }: { page: TreePage }) {
  const locale = useChatInterfaceLocale();
  const copy = useNamespaceCopy(runtimeCopy);
  if (!page.openSuggestions) return null;
  return (
    <span
      className="ws-wiki-tree-count"
      title={plural(page.openSuggestions, copy.openSuggestion, undefined, locale)}
      aria-label={plural(page.openSuggestions, copy.openSuggestion, undefined, locale)}
    >
      {page.openSuggestions}
    </span>
  );
}

function Row({
  page,
  depth,
  index,
  selectedId,
  expanded,
  onToggle,
  onSelect,
}: {
  page: TreePage;
  depth: number;
  index: TreeIndex;
  selectedId: string | null;
  expanded: Set<string>;
  onToggle: (id: string) => void;
  onSelect: (id: string) => void;
}) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const editor = useNamespaceCopy(editorCopy);
  const children = index.children(page.id);
  const open = page.isRoot || expanded.has(page.id);
  const selected = page.id === selectedId;
  return (
    <li>
      <div
        className="ws-wiki-tree-row"
        data-selected={selected ? 'true' : undefined}
        data-root={page.isRoot ? 'true' : undefined}
        style={{ paddingInlineStart: `${6 + depth * 16}px` }}
      >
        {children.length > 0 && !page.isRoot ? (
          <button
            type="button"
            className="ws-wiki-tree-toggle"
            aria-expanded={open}
            aria-label={formatCopy(editor.treeToggle, {
              action: open ? editor.treeHide : editor.treeShow,
              title: page.title,
            })}
            onClick={() => onToggle(page.id)}
          >
            <WorkspaceIcon name={open ? 'down' : 'chevron'} size={12} />
          </button>
        ) : (
          <span className="ws-wiki-tree-toggle" aria-hidden="true">
            {page.isRoot && <WorkspaceIcon name="home" size={14} />}
          </span>
        )}
        <button
          type="button"
          className="ws-wiki-tree-page"
          aria-current={selected ? 'page' : undefined}
          onClick={() => onSelect(page.id)}
        >
          <span className="ws-wiki-tree-title">{page.title}</span>
          <Count page={page} />
        </button>
      </div>
      {open && children.length > 0 && (
        <ul>
          {children.map((child) => (
            <Row
              key={child.id}
              page={child}
              depth={depth + 1}
              index={index}
              selectedId={selectedId}
              expanded={expanded}
              onToggle={onToggle}
              onSelect={onSelect}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

/**
 * The page tree: the root first, every other page under its parent, with the
 * open-suggestion count each page has. A search narrows it to matching titles.
 */
export function TreeNav({
  index,
  selectedId,
  onSelect,
  onNewPage,
}: {
  index: TreeIndex;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onNewPage: (() => void) | null;
}) {
  const copy = useNamespaceCopy(workspaceFeaturesCopy);
  const knowledgeCopy = useNamespaceCopy(knowledgePagesCopy);
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(
    () => new Set(selectedId ? index.ancestors(selectedId).map((page) => page.id) : []),
  );
  // The selected page's branch opens as it is selected, wherever it was chosen.
  const [shownFor, setShownFor] = useState(selectedId);
  if (selectedId !== shownFor) {
    setShownFor(selectedId);
    if (selectedId) {
      const path = index.ancestors(selectedId).map((page) => page.id);
      if (path.some((id) => !expanded.has(id))) setExpanded(new Set([...expanded, ...path]));
    }
  }
  const toggle = (id: string) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const needle = query.trim();
  const matches = needle ? index.flat().filter(({ page }) => matchTitle(page.title, needle)) : [];
  return (
    <nav className="ws-wiki-tree" aria-label={copy.knowledgePages}>
      <label className="ws-search-field ws-wiki-search">
        <WorkspaceIcon name="search" size={14} />
        <span className="ws-sr-only">{copy.searchPages}</span>
        <input
          type="search"
          value={query}
          placeholder={copy.searchPages}
          onChange={(event) => setQuery(event.target.value)}
          onInput={(event) => setQuery((event.target as HTMLInputElement).value)}
        />
      </label>
      {needle ? (
        matches.length ? (
          <ul className="ws-wiki-tree-list">
            {matches.map(({ page }) => (
              <li key={page.id}>
                <div
                  className="ws-wiki-tree-row"
                  data-selected={page.id === selectedId ? 'true' : undefined}
                >
                  <button
                    type="button"
                    className="ws-wiki-tree-page"
                    aria-current={page.id === selectedId ? 'page' : undefined}
                    onClick={() => onSelect(page.id)}
                  >
                    <span className="ws-wiki-tree-title">
                      {page.title}
                      <small>{index.ancestors(page.id).at(-1)?.title}</small>
                    </span>
                    <Count page={page} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="ws-muted ws-wiki-tree-none" role="status">
            {formatCopy(knowledgeCopy.noMatch, { filter: needle })}
          </p>
        )
      ) : (
        <ul className="ws-wiki-tree-list">
          <Row
            page={index.root}
            depth={0}
            index={index}
            selectedId={selectedId}
            expanded={expanded}
            onToggle={toggle}
            onSelect={onSelect}
          />
        </ul>
      )}
      {onNewPage && (
        <button type="button" className="ws-wiki-tree-new" onClick={onNewPage}>
          <WorkspaceIcon name="plus" size={14} />
          {knowledgeCopy.kindCreatePage}
        </button>
      )}
      {index.archived.length > 0 && (
        <details className="ws-wiki-archived">
          <summary>{plural(index.archived.length, 'archived page')}</summary>
          <ul className="ws-wiki-tree-list">
            {index.archived.map((page) => (
              <li key={page.id}>
                <div
                  className="ws-wiki-tree-row"
                  data-selected={page.id === selectedId ? 'true' : undefined}
                >
                  <button
                    type="button"
                    className="ws-wiki-tree-page"
                    aria-current={page.id === selectedId ? 'page' : undefined}
                    onClick={() => onSelect(page.id)}
                  >
                    <span className="ws-wiki-tree-title">{page.title}</span>
                    <Count page={page} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </details>
      )}
    </nav>
  );
}
